/* =============================================================================
   PaginatedTable — the §11 paginated table
   -----------------------------------------------------------------------------
   The page supplies the column config and the data source. The table owns the
   page, page size, sort, filters, column visibility, loading and error state
   internally and issues every request through the page's `dataSource`.

   Everything the contract assigns to the server stays on the server: this file
   contains NO client-side filter, sort, pagination or aggregation. Sorting,
   filtering and paging only ever change the `TableQuery` it sends, and the
   numbers it renders come from `TableResult`.

   @tanstack/react-table v9 is used as the headless engine: it owns the column
   model, the column-visibility state and the sorting state, all of which are
   driven in manual mode (`manualSorting`) over the server-side contract.

   Persistence keys (page-owned, never the row key, never a column key):
     - `dhdtech:table:columns:<id>`   `{ version, columns: string[] }`
     - `dhdtech:table:pageSize:<id>`  number

   Deliberate decisions, each with its evidence:
     - `date` filterType emits `between [<day>T00:00:00, <day>T23:59:59]`
       rather than `eq`. The fixtures are full ISO-8601 datetimes
       (`fixtures.ts` `isoDate()` → `2026-05-03T00:00:00`), so an `eq` against
       a bare `2026-05-03` matches nothing. `matchesFilter`'s `between` falls to
       string comparison for non-numeric cells (mockApi.ts), which makes the
       closed day span exactly the right predicate — it matches a row whatever
       its time-of-day.
     - `datetime` filterType emits `eq` on the full `YYYY-MM-DDTHH:MM:00`.
     - The `select` filter's listbox is rendered INSIDE the filter surface, not
       portaled. That removes the nested-overlay problem the reference catalogues
       (an outside-close triggered from inside another overlay, or a menu clipped
       by the surface's own `overflow-y-auto`) instead of guarding against it.
     - Typed date entry with the locale placeholder, not a calendar. `format.ts`
       ships `datePlaceholder()` and `parseDateInput()` as a pair precisely for
       typed entry, and every date filter value is validated by the same parser
       the §10 date input uses.
   ============================================================================= */

import { useCallback, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { Checkbox } from '@base-ui/react/checkbox';
import { Dialog } from '@base-ui/react/dialog';
import { Popover } from '@base-ui/react/popover';
import {
  IconArrowsSort,
  IconCaretDownFilled,
  IconCaretUpFilled,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
  IconColumns3,
  IconFilter,
  IconSearch,
  IconX,
} from '@tabler/icons-react';
import { columnVisibilityFeature, rowSortingFeature, tableFeatures, useTable } from '@tanstack/react-table';
import type { ColumnVisibilityState, OnChangeFn, SortingState } from '@tanstack/react-table';

import type { AggregationOp, AggregationSpec, DataSource, FilterSpec, RawCellValue, RemoteSearch, Row, SortSpec, TableQuery, TableResult } from '../../data/types';
import {
  datePlaceholder,
  formatDate,
  formatMoney,
  formatNumber,
  formatPercentage,
  parseDateInput,
  parseMoney,
  parseNumber,
  parsePercentage,
  parseTimeInput,
  resolveLocale,
} from '../../i18n/format';
import type { LocaleInput } from '../../i18n/format';
import { useTranslation } from '../../i18n/LocaleProvider';
import { cn } from '../../lib/cn';
import { PRESS } from '../../interaction';
import { useRemoteSearch } from '../../lib/useRemoteSearch';
import { EmptyState, ErrorState, SkeletonTable } from '../feedback';
import { Label } from '../forms/text/Label';

/* -----------------------------------------------------------------------------
   Column configuration
   -------------------------------------------------------------------------- */

export type TableColumnAlign = 'left' | 'right' | 'center';

export type TableFilterType =
  | 'text'
  | 'money'
  | 'moneyRange'
  | 'percentage'
  | 'percentageRange'
  | 'number'
  | 'numberRange'
  | 'date'
  | 'dateRange'
  | 'datetime'
  | 'datetimeRange'
  | 'select';

export interface TableColumnAggregate {
  op: AggregationOp;
  /** Rounding told to the API for `avg` (and optionally `sum`). */
  precision?: number;
  /** A fixed footer label for this column's subtotal, instead of the value. */
  label?: string;
}

export interface TableColumn {
  /** Unique column id. Reads the cell from the row, and is the `field` in every request. */
  key: string;
  header: string;
  sortable?: boolean;
  filterable?: boolean;
  filterType?: TableFilterType;
  width?: number | string;
  align?: TableColumnAlign;
  /** Presentation only — the raw value stays the typed value. */
  formatter?: (value: RawCellValue, row?: Row) => string;
  aggregate?: TableColumnAggregate;
  hiddenByDefault?: boolean;
}

/** The remote search a `select` filter runs against. API-side, never local. */
export interface TableSelectSource {
  search: RemoteSearch;
  minSearchLength?: number;
  placeholder?: string;
}

export interface PaginatedTableProps<T extends Row = Row>
  extends Omit<ComponentPropsWithRef<'div'>, 'id' | 'children'> {
  /** Unique and stable per table instance. Used ONLY as the persistence key. */
  id: string;
  /** The table's accessible title (`<caption>`). */
  caption: string;
  columns: TableColumn[];
  dataSource: DataSource<T>;
  /** The column key whose value is unique per row. Defaults to the first column. */
  rowKey?: string;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  /** Bump when the column set changes in a breaking way — a mismatch discards the stored selection. */
  columnsVersion?: number;
  /** Remote search sources, keyed by column key, for `filterType: 'select'`. */
  selectSources?: Record<string, TableSelectSource>;
  locale?: LocaleInput;
  /** Called with every query the table issues, so the page can sync its own state. */
  onQueryChange?: (query: TableQuery) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  errorTitle?: string;
}

/* -----------------------------------------------------------------------------
   Persistence — every read is total: corrupt JSON, an unknown key, a version
   mismatch or storage being unavailable all fall back to the defaults.
   -------------------------------------------------------------------------- */

const COLUMNS_KEY = (id: string) => `dhdtech:table:columns:${id}`;
const PAGE_SIZE_KEY = (id: string) => `dhdtech:table:pageSize:${id}`;

const RANGE_TYPES: TableFilterType[] = ['moneyRange', 'percentageRange', 'numberRange', 'dateRange', 'datetimeRange'];
const NUMERIC_TYPES: TableFilterType[] = ['money', 'percentage', 'number', ...RANGE_TYPES];

function filterTypeOf(column: TableColumn): TableFilterType {
  return column.filterType ?? 'text';
}

function alignOf(column: TableColumn): TableColumnAlign {
  if (column.align) return column.align;
  return NUMERIC_TYPES.includes(filterTypeOf(column)) ? 'right' : 'left';
}

/** The default selection: every column unless `hiddenByDefault`. The row key is always in. */
function defaultVisibility(columns: TableColumn[], rowKeyColumn: string): ColumnVisibilityState {
  const next: ColumnVisibilityState = {};
  for (const column of columns) next[column.key] = column.hiddenByDefault !== true;
  next[rowKeyColumn] = true;
  return next;
}

/**
 * Reads the stored column selection. Intersects with the real column keys, so a
 * key that no longer exists is dropped rather than rendered, and the `rowKey`
 * column can never be hidden by a stored value.
 */
function readVisibility(
  id: string,
  columns: TableColumn[],
  columnsVersion: number,
  rowKeyColumn: string,
): ColumnVisibilityState {
  const fallback = defaultVisibility(columns, rowKeyColumn);

  let raw: string | null;
  try {
    raw = window.localStorage.getItem(COLUMNS_KEY(id));
  } catch {
    return fallback;
  }
  if (!raw) return fallback;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return fallback; // corrupt JSON is "no persisted selection", not a crash
  }
  if (typeof parsed !== 'object' || parsed === null) return fallback;

  const record = parsed as { version?: unknown; columns?: unknown };
  if (record.version !== columnsVersion) return fallback; // never migrate between versions
  if (!Array.isArray(record.columns)) return fallback;

  const known = new Set(columns.map((column) => column.key));
  const stored: unknown[] = record.columns;
  const visible = new Set(
    stored.filter((key): key is string => typeof key === 'string' && known.has(key)),
  );

  const next: ColumnVisibilityState = {};
  for (const column of columns) next[column.key] = visible.has(column.key);
  next[rowKeyColumn] = true;
  return next;
}

/** Display order is the config order: the selector has no reorder affordance, so
 *  the array is only ever rewritten in the same ordering it was read with. */
function writeVisibility(
  id: string,
  columns: TableColumn[],
  visibility: ColumnVisibilityState,
  version: number,
): void {
  const keys = columns
    .filter((column) => visibility[column.key] !== false)
    .map((column) => column.key);
  try {
    window.localStorage.setItem(COLUMNS_KEY(id), JSON.stringify({ version, columns: keys }));
  } catch {
    /* storage unavailable (private mode, quota) — the selection just is not persisted */
  }
}

function readPageSize(id: string, options: number[], fallback: number): number {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(PAGE_SIZE_KEY(id));
  } catch {
    return fallback;
  }
  if (!raw) return fallback;
  const parsed = Number(raw);
  return options.includes(parsed) ? parsed : fallback;
}

function writePageSize(id: string, size: number): void {
  try {
    window.localStorage.setItem(PAGE_SIZE_KEY(id), String(size));
  } catch {
    /* storage unavailable — the size just is not persisted */
  }
}

/* -----------------------------------------------------------------------------
   Small internals
   -------------------------------------------------------------------------- */

const TABLE_FEATURES = tableFeatures({ rowSortingFeature, columnVisibilityFeature });

const NARROW_QUERY = '(max-width: 639px)';

function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onStoreChange);
      return () => mql.removeEventListener('change', onStoreChange);
    },
    [query],
  );
  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query]);
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

function cellText(value: RawCellValue): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

/** Replaces a field's filter, or removes it when `spec` is null. A field
 *  carries at most one active filter, so the prior one never compounds. */
function replaceFieldFilter(filters: FilterSpec[], field: string, spec: FilterSpec | null): FilterSpec[] {
  const next = filters.filter((filter) => filter.field !== field);
  if (spec) next.push(spec);
  return next;
}

/**
 * The message the failure itself carried, or `null` when it carried none.
 *
 * The generic fallback is deliberately NOT built here. This runs at module
 * scope, where there is no translator, and it runs once — a sentence frozen here
 * would keep the language it was born in for as long as the error is on screen,
 * including across a language switch. `null` lets the render site supply the
 * fallback, where it is re-derived on every render and can therefore be
 * translated.
 */
function messageOf(caught: unknown): string | null {
  if (caught instanceof Error && caught.message) return caught.message;
  return null;
}

/* -----------------------------------------------------------------------------
   Filter drafts — raw text in, a validated FilterSpec out
   -------------------------------------------------------------------------- */

interface FilterDraft {
  /** Single value, range lower bound, or date. */
  a: string;
  /** Range upper bound. */
  b: string;
  /** Time for a datetime lower bound. */
  timeA: string;
  /** Time for a datetime upper bound. */
  timeB: string;
  /** Members for a `select` filter. */
  values: string[];
}

const EMPTY_DRAFT: FilterDraft = { a: '', b: '', timeA: '', timeB: '', values: [] };

type DraftMode = 'text' | 'number' | 'date' | 'datetime';

function draftModeFor(column: TableColumn): DraftMode {
  const type = filterTypeOf(column);
  if (type === 'text') return 'text';
  if (type === 'date' || type === 'dateRange') return 'date';
  if (type === 'datetime' || type === 'datetimeRange') return 'datetime';
  return 'number';
}

function isRange(column: TableColumn): boolean {
  return RANGE_TYPES.includes(filterTypeOf(column));
}

/** The decimal places a value actually carries, so a rehydrated editor shows the
 *  value it holds rather than the formatter's default rounding. */
function decimalsOf(value: number): number {
  const text = String(value);
  const point = text.indexOf('.');
  return point === -1 ? 0 : Math.min(text.length - point - 1, 6);
}

/**
 * The text a numeric editor opens with. It MUST be the output of the formatter
 * whose parser validates it again on Apply: `String(value)` is a JavaScript
 * literal, and in a comma-decimal locale (`pt-BR`, `de-DE`, `es-ES`, `fr-FR`)
 * `parseLocalizedNumber` deletes `.` as the GROUP separator — so reopening a
 * `[12.5, 20.5]` range and pressing Apply without editing anything would emit
 * `[125, 205]` and silently change the query.
 */
function numberDraft(column: TableColumn, value: number, locale: string): string {
  const type = filterTypeOf(column);
  if (type === 'money' || type === 'moneyRange') return formatMoney(value, { locale });
  if (type === 'percentage' || type === 'percentageRange') return formatPercentage(value, { locale });
  return formatNumber(value, { locale, precision: decimalsOf(value) });
}

/** An ISO value's day, written in the locale's own order — `MM/DD/YYYY` in
 *  `en-US`, `DD/MM/YYYY` in `pt-BR` — so the very same `parseDateInput` that
 *  validates it on Apply can read it back. A raw `slice(0, 10)` cannot: the
 *  parser rejects an ISO day as an out-of-range month or day. */
function dayDraft(iso: string, locale: string): string {
  return formatDate(iso, { locale, style: 'short' });
}

/** Rehydrates the editor from an active filter, so reopening shows what is applied. */
function draftFromFilter(column: TableColumn, spec: FilterSpec | undefined, locale: string): FilterDraft {
  if (!spec) return EMPTY_DRAFT;
  const type = filterTypeOf(column);

  if (spec.op === 'in') {
    return { ...EMPTY_DRAFT, values: Array.isArray(spec.value) ? spec.value.map(String) : [] };
  }

  if (spec.op === 'between' && Array.isArray(spec.value)) {
    const [lo, hi] = spec.value as [unknown, unknown];
    const from = typeof lo === 'string' ? lo : '';
    const to = typeof hi === 'string' ? hi : '';

    if (type === 'datetimeRange') {
      return {
        ...EMPTY_DRAFT,
        a: dayDraft(from, locale),
        timeA: from.slice(11, 16),
        b: dayDraft(to, locale),
        timeB: to.slice(11, 16),
      };
    }
    if (type === 'dateRange') {
      return { ...EMPTY_DRAFT, a: dayDraft(from, locale), b: dayDraft(to, locale) };
    }
    // A `date` filter is ALSO a `between` (the closed calendar day, see
    // `buildFilter`), so it must be caught here. Falling through to the numeric
    // branch would leave the field blank — and a no-op Apply would then emit
    // `NO_FILTER` and silently delete the filter.
    if (type === 'date') {
      return { ...EMPTY_DRAFT, a: dayDraft(from, locale) };
    }

    const lower = Number(lo);
    const upper = Number(hi);
    return {
      ...EMPTY_DRAFT,
      a: Number.isFinite(lower) ? numberDraft(column, lower, locale) : '',
      b: Number.isFinite(upper) ? numberDraft(column, upper, locale) : '',
    };
  }

  const value = spec.value;
  if (typeof value === 'string' && type === 'date') return { ...EMPTY_DRAFT, a: dayDraft(value, locale) };
  if (typeof value === 'string' && type === 'datetime') {
    return { ...EMPTY_DRAFT, a: dayDraft(value, locale), timeA: value.slice(11, 16) };
  }
  if (typeof value === 'number') return { ...EMPTY_DRAFT, a: numberDraft(column, value, locale) };
  if (typeof value === 'string') return { ...EMPTY_DRAFT, a: value };
  return EMPTY_DRAFT;
}

/** Which editor field an error belongs to, so exactly that one is marked invalid
 *  and wired to the message through `aria-describedby`. */
type DraftSlot = 'a' | 'b' | 'timeA' | 'timeB';

interface BuildResult {
  ok: boolean;
  spec: FilterSpec | null;
  /** A catalogue KEY, not a message: `buildFilter` runs at module scope, where
   *  the translator is unreachable, so the message is resolved at the render
   *  site that displays it. */
  error: string | null;
  slot: DraftSlot | null;
}

const NO_FILTER: BuildResult = { ok: true, spec: null, error: null, slot: null };

function withSpec(spec: FilterSpec): BuildResult {
  return { ok: true, spec, error: null, slot: null };
}

function errorAt(slot: DraftSlot, error: string): BuildResult {
  return { ok: false, spec: null, error, slot };
}

function buildFilter(column: TableColumn, draft: FilterDraft, locale: string): BuildResult {
  const type = filterTypeOf(column);
  const field = column.key;

  if (type === 'select') {
    if (draft.values.length === 0) return NO_FILTER;
    // Membership, not equality — the multi-select gotcha in §11.
    return withSpec({ field, op: 'in', value: draft.values });
  }

  const parseNumberFor = (raw: string): number | null => {
    if (type === 'money' || type === 'moneyRange') return parseMoney(raw, { locale });
    if (type === 'percentage' || type === 'percentageRange') return parsePercentage(raw, { locale });
    return parseNumber(raw, { locale });
  };

  if (type === 'text') {
    const value = draft.a.trim();
    return value ? withSpec({ field, op: 'contains', value }) : NO_FILTER;
  }

  if (isRange(column)) {
    if (!draft.a.trim()) return errorAt('a', 'ds.table.errorBothBounds');
    if (!draft.b.trim()) return errorAt('b', 'ds.table.errorBothBounds');

    if (type === 'dateRange') {
      const from = parseDateInput(draft.a, locale);
      const to = parseDateInput(draft.b, locale);
      if (!from) return errorAt('a', 'ds.table.errorStartDate');
      if (!to) return errorAt('b', 'ds.table.errorEndDate');
      if (from > to) return errorAt('b', 'ds.table.errorStartBeforeEnd');
      return withSpec({ field, op: 'between', value: [`${from}T00:00:00`, `${to}T23:59:59`] });
    }

    if (type === 'datetimeRange') {
      const from = parseDateInput(draft.a, locale);
      const to = parseDateInput(draft.b, locale);
      const fromTime = parseTimeInput(draft.timeA);
      const toTime = parseTimeInput(draft.timeB);
      if (!from) return errorAt('a', 'ds.table.errorStartDate');
      if (!fromTime) return errorAt('timeA', 'ds.table.errorStartTime');
      if (!to) return errorAt('b', 'ds.table.errorEndDate');
      if (!toTime) return errorAt('timeB', 'ds.table.errorEndTime');
      const lower = `${from}T${fromTime}`;
      const upper = `${to}T${toTime}`;
      if (lower > upper) return errorAt('timeB', 'ds.table.errorStartAfterEnd');
      return withSpec({ field, op: 'between', value: [lower, upper] });
    }

    const lo = parseNumberFor(draft.a);
    const hi = parseNumberFor(draft.b);
    if (lo === null) return errorAt('a', 'ds.table.errorLowerBound');
    if (hi === null) return errorAt('b', 'ds.table.errorUpperBound');
    if (type === 'percentageRange' && (lo < 0 || hi > 100)) {
      // Name the field that actually holds the out-of-range text.
      return errorAt(lo < 0 ? 'a' : 'b', 'ds.table.errorPercentageRange');
    }
    if (lo > hi) return errorAt('b', 'ds.table.errorBoundsOrder');
    return withSpec({ field, op: 'between', value: [lo, hi] });
  }

  if (type === 'date') {
    if (!draft.a.trim()) return NO_FILTER;
    const day = parseDateInput(draft.a, locale);
    if (!day) return errorAt('a', 'ds.table.errorValidDate');
    // The whole calendar day, so a row matches whatever its time-of-day.
    return withSpec({ field, op: 'between', value: [`${day}T00:00:00`, `${day}T23:59:59`] });
  }

  if (type === 'datetime') {
    if (!draft.a.trim() && !draft.timeA.trim()) return NO_FILTER;
    const day = parseDateInput(draft.a, locale);
    if (!day) return errorAt('a', 'ds.table.errorValidDate');
    const time = parseTimeInput(draft.timeA);
    if (!time) return errorAt('timeA', 'ds.table.errorValidTime');
    return withSpec({ field, op: 'eq', value: `${day}T${time}` });
  }

  if (!draft.a.trim()) return NO_FILTER;
  const value = parseNumberFor(draft.a);
  if (value === null) return errorAt('a', 'ds.table.errorValidNumber');
  if (type === 'percentage' && (value < 0 || value > 100)) {
    return errorAt('a', 'ds.table.errorPercentageRange');
  }
  return withSpec({ field, op: 'eq', value });
}

/* -----------------------------------------------------------------------------
   The filter surface: an anchored popover on wide viewports, a full-width
   bottom sheet on narrow ones. Both are modal, so both move focus in, trap it,
   close on Escape and return focus to the trigger.
   -------------------------------------------------------------------------- */

interface FilterSurfaceProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isNarrow: boolean;
  triggerClassName: string;
  triggerLabel: string;
  triggerContent: ReactNode;
  panelTitle: string;
  panelDescription?: string;
  children: ReactNode;
  footer: ReactNode;
}

function FilterSurface({
  open,
  onOpenChange,
  isNarrow,
  triggerClassName,
  triggerLabel,
  triggerContent,
  panelTitle,
  panelDescription,
  children,
  footer,
}: FilterSurfaceProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();

  // Each branch renders its own Base UI Close, which is also what arms the
  // focus trap — and it gives the dialog pattern its visible way out.
  const panel = (close: ReactNode) => (
    <>
      <div className="flex items-start justify-between gap-3 border-b border-line-subtle px-4 py-3">
        <div className="min-w-0">
          <p id={titleId} className="font-display text-sm font-semibold uppercase tracking-title text-heading">
            {panelTitle}
          </p>
          {panelDescription ? (
            <p id={descriptionId} className="mt-0.5 text-2xs text-muted">
              {panelDescription}
            </p>
          ) : null}
        </div>
        {close}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">{children}</div>
      <div className="flex items-center justify-between gap-3 border-t border-line-subtle px-4 py-3">{footer}</div>
    </>
  );

  const closeClass = cn(
    'dhd-target relative inline-grid size-7 shrink-0 place-items-center rounded-xs text-muted',
    'hover:bg-surface-muted hover:text-heading',
    PRESS,
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
  );

  const closeButton = (
    <>
      <IconX className="size-4" aria-hidden="true" />
    </>
  );

  if (isNarrow) {
    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange} modal>
        <Dialog.Trigger className={triggerClassName} aria-label={triggerLabel}>
          {triggerContent}
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 z-[var(--z-overlay)] bg-overlay" />
          <Dialog.Popup
            aria-labelledby={titleId}
            aria-describedby={panelDescription ? descriptionId : undefined}
            className={cn(
              // The narrow-viewport sheet is a modal at the modal rung, and its
              // elevation is the same hard offset as every other raised surface
              // — the soft `shadow-xl` it used is the blur this language replaced.
              'fixed inset-x-0 bottom-0 z-[var(--z-modal)] flex max-h-[85dvh] w-full flex-col',
              'rounded-t-xs border-t-2 border-line-strong bg-surface-elevated shadow-hard',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
            )}
          >
            <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-pill bg-line" aria-hidden="true" />
            {panel(
              <Dialog.Close className={closeClass} aria-label={t('ds.table.closePanel')}>
                {closeButton}
              </Dialog.Close>,
            )}
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange} modal="trap-focus">
      <Popover.Trigger className={triggerClassName} aria-label={triggerLabel}>
        {triggerContent}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner side="bottom" align="end" sideOffset={6} collisionPadding={10}>
          <Popover.Popup
            aria-labelledby={titleId}
            aria-describedby={panelDescription ? descriptionId : undefined}
            className={cn(
              // The column/filter surface is an anchored popover, so it takes
              // the popover rung and the same hard offset as Menu and Popover.
              'z-[var(--z-popover)] flex max-h-[70dvh] w-[340px] max-w-[calc(100vw-1.5rem)] flex-col',
              'rounded-xs border border-line-strong bg-surface-elevated shadow-hard',
            )}
          >
            {panel(
              <Popover.Close className={closeClass} aria-label={t('ds.table.closePanel')}>
                {closeButton}
              </Popover.Close>,
            )}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

/* -----------------------------------------------------------------------------
   Filter editors
   -------------------------------------------------------------------------- */

/** The filter popover reuses the §10 inputs at table proportions. They are sized
 *  to `--target-min` (44px) so the CONVENTIONS §6 hit-target rule is met by the
 *  control itself — a text `<input>` cannot carry the `dhd-target` overlay,
 *  because replaced elements do not render `::after`. */
const FIELD_CLASS = cn(
  // The height stays at `--target-min` rather than the forms family's 40px
  // BECAUSE the control cannot carry the `dhd-target` overlay: a replaced
  // element does not render `::after`, so this input has to be 44px itself.
  // The border width and colour are the family's, so the box still reads as the
  // same control as the field beside it in the pagination row.
  'h-[var(--target-min)] w-full rounded-xs border-[length:var(--border-w)] border-line-strong bg-surface px-2.5 text-sm text-heading',
  'placeholder:text-muted',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
  'aria-[invalid=true]:border-danger',
);

interface FieldProps {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  invalid?: boolean;
  /** The id of the message explaining `invalid`, wired through `aria-describedby`. */
  describedBy?: string;
  inputMode?: 'numeric' | 'decimal' | 'text';
}

function Field({ label, value, onValueChange, placeholder, invalid, describedBy, inputMode }: FieldProps) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <input
        id={id}
        type="text"
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? describedBy : undefined}
        onChange={(event) => onValueChange(event.target.value)}
        className={FIELD_CLASS}
      />
    </div>
  );
}

/** The `select` filter: a combobox over the API-side remote search. */
function SelectEditor({
  source,
  column,
  selected,
  onSelectedChange,
}: {
  source: TableSelectSource;
  column: TableColumn;
  selected: string[];
  onSelectedChange: (values: string[]) => void;
}) {
  const { t } = useTranslation();
  const listId = useId();
  const [activeIndex, setActiveIndex] = useState(0);
  const {
    options,
    truncated,
    loading,
    error,
    query,
    setQuery,
    isBelowMinLength,
  } = useRemoteSearch({
    search: source.search,
    minSearchLength: source.minSearchLength ?? 0,
    debounceMs: 300,
    /*
       A source that permits a zero-length search is declaring the empty term to
       be a valid query — which for a filter means "the whole option list". Left
       at the hook's default the editor short-circuits on the empty term and
       opens to nothing, so a reader clicks a filter, sees an empty panel, and
       concludes the control is broken. Reproduced before the fix: a select
       filter with `minSearchLength: 0` showed no options until a character was
       typed.

       A source that requires characters is a different case and keeps the old
       behaviour: an empty term is not a valid query, so the panel correctly says
       how many characters it needs instead of guessing at a subset.
    */
    initialLoad: (source.minSearchLength ?? 0) === 0,
  });

  const visible = useMemo(
    () => options.filter((option) => !selected.includes(option.value)),
    [options, selected],
  );

  // A remote result that lands between keypresses can leave `activeIndex` past
  // the end of a shorter list, which would highlight nothing and make Enter a
  // no-op. Clamping on read keeps the cursor valid without a catch-up effect.
  const active = visible.length === 0 ? -1 : Math.min(activeIndex, visible.length - 1);

  const toggle = (value: string) => {
    onSelectedChange(
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value],
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${listId}-input`}>{column.header}</Label>
        <div className="relative">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" aria-hidden="true">
            <IconSearch className="size-4" />
          </span>
          <input
            id={`${listId}-input`}
            type="text"
            role="combobox"
            autoComplete="off"
            value={query}
            placeholder={source.placeholder ?? t('ds.table.search')}
            aria-expanded={visible.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={visible[active] ? `${listId}-${active}` : undefined}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveIndex((index) => Math.min(index + 1, Math.max(visible.length - 1, 0)));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveIndex((index) => Math.max(index - 1, 0));
              } else if (event.key === 'Enter') {
                const option = visible[active];
                if (option) {
                  event.preventDefault();
                  toggle(option.value);
                }
              } else if (event.key === 'Escape' && query !== '') {
                // Escape clears the term first; with an empty term it is left to
                // the surface, which closes and returns focus to the trigger.
                event.preventDefault();
                event.stopPropagation();
                setQuery('');
              }
            }}
            className={cn(FIELD_CLASS, 'pl-8')}
          />
        </div>
      </div>

      {selected.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={t('ds.table.selectedValues')}>
          {selected.map((value) => (
            <li key={value}>
              <button
                type="button"
                onClick={() => toggle(value)}
                className={cn(
                  // A removable chip is 24px tall inside a `gap-1.5` wrap — a 44px
                  // overlay would collide with the row above and with its
                  // neighbour, which is the cluster case `dhd-target-tight` exists
                  // for. The chip is also wider than 44px, so the 24px floor is
                  // met by the control itself.
                  'dhd-target-tight relative inline-flex items-center gap-1 rounded-xs',
                  'border border-line bg-surface-muted px-2 py-1 text-2xs text-body',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                )}
              >
                {value}
                <IconX className="size-3" aria-hidden="true" />
                <span className="dhd-sr-only">{t('ds.table.removeValue', { value })}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {isBelowMinLength ? (
        <p className="text-2xs text-muted">
          {t('ds.table.minSearchLength', { count: source.minSearchLength ?? 0 })}
        </p>
      ) : null}

      {loading ? (
        <p role="status" className="text-2xs text-muted">
          {t('ds.table.searching')}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="text-2xs text-danger">
          {error}
        </p>
      ) : null}

      {!loading && !error && !isBelowMinLength && query !== '' && visible.length === 0 ? (
        <p className="text-2xs text-muted">{t('ds.table.noMatches')}</p>
      ) : null}

      <ul id={listId} role="listbox" aria-label={`${column.header} values`} className="max-h-48 overflow-y-auto">
        {visible.map((option, index) => (
          <li
            key={option.value}
            id={`${listId}-${index}`}
            role="option"
            aria-selected={false}
            tabIndex={-1}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => toggle(option.value)}
            className={cn(
              'flex cursor-pointer flex-col gap-0.5 rounded-xs px-2 py-1.5 text-sm',
              index === active ? 'bg-surface-muted text-heading' : 'text-body',
            )}
          >
            <span>{option.label}</span>
            {option.description ? <span className="text-2xs text-muted">{option.description}</span> : null}
          </li>
        ))}
      </ul>

      {truncated ? <p className="text-2xs text-muted">{t('ds.table.refineSearch')}</p> : null}
    </div>
  );
}

function FilterEditor({
  column,
  draft,
  onDraftChange,
  errorSlot,
  errorId,
  locale,
  selectSource,
}: {
  column: TableColumn;
  draft: FilterDraft;
  onDraftChange: (draft: FilterDraft) => void;
  errorSlot: DraftSlot | null;
  /** The id of the filter's error message, for `aria-describedby`. */
  errorId: string;
  locale: string;
  selectSource?: TableSelectSource;
}) {
  const { t } = useTranslation();
  const type = filterTypeOf(column);
  const mode = draftModeFor(column);

  if (type === 'select') {
    if (!selectSource) {
      return <p className="text-sm text-muted">{t('ds.table.noSearchSource')}</p>;
    }
    return (
      <SelectEditor
        source={selectSource}
        column={column}
        selected={draft.values}
        onSelectedChange={(values) => onDraftChange({ ...draft, values })}
      />
    );
  }

  // Only the field the message actually names is marked invalid, so a range with
  // one bad bound does not paint both inputs red.
  const slotProps = (slot: DraftSlot) => ({
    invalid: errorSlot === slot,
    describedBy: errorId,
  });

  const numberPlaceholder = type === 'money' || type === 'moneyRange' ? '0.00' : type.includes('percentage') ? '0' : '0';

  if (isRange(column)) {
    if (type === 'dateRange') {
      return (
        <div className="flex flex-col gap-3">
          <Field label={t('ds.table.from')} value={draft.a} onValueChange={(value) => onDraftChange({ ...draft, a: value })} placeholder={datePlaceholder(locale)} {...slotProps('a')} />
          <Field label={t('ds.table.to')} value={draft.b} onValueChange={(value) => onDraftChange({ ...draft, b: value })} placeholder={datePlaceholder(locale)} {...slotProps('b')} />
        </div>
      );
    }
    if (type === 'datetimeRange') {
      return (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-[1.4fr_1fr] gap-2">
            <Field label={t('ds.table.fromDate')} value={draft.a} onValueChange={(value) => onDraftChange({ ...draft, a: value })} placeholder={datePlaceholder(locale)} {...slotProps('a')} />
            <Field label={t('ds.table.time')} value={draft.timeA} onValueChange={(value) => onDraftChange({ ...draft, timeA: value })} placeholder={t('ds.table.timePlaceholder')} {...slotProps('timeA')} />
          </div>
          <div className="grid grid-cols-[1.4fr_1fr] gap-2">
            <Field label={t('ds.table.toDate')} value={draft.b} onValueChange={(value) => onDraftChange({ ...draft, b: value })} placeholder={datePlaceholder(locale)} {...slotProps('b')} />
            <Field label={t('ds.table.time')} value={draft.timeB} onValueChange={(value) => onDraftChange({ ...draft, timeB: value })} placeholder={t('ds.table.timePlaceholder')} {...slotProps('timeB')} />
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-3">
        <Field label={t('ds.table.from')} value={draft.a} onValueChange={(value) => onDraftChange({ ...draft, a: value })} placeholder={numberPlaceholder} inputMode="decimal" {...slotProps('a')} />
        <Field label={t('ds.table.to')} value={draft.b} onValueChange={(value) => onDraftChange({ ...draft, b: value })} placeholder={numberPlaceholder} inputMode="decimal" {...slotProps('b')} />
      </div>
    );
  }

  if (type === 'datetime') {
    return (
      <div className="grid grid-cols-[1.4fr_1fr] gap-2">
        <Field label={t('ds.table.date')} value={draft.a} onValueChange={(value) => onDraftChange({ ...draft, a: value })} placeholder={datePlaceholder(locale)} {...slotProps('a')} />
        <Field label={t('ds.table.time')} value={draft.timeA} onValueChange={(value) => onDraftChange({ ...draft, timeA: value })} placeholder={t('ds.table.timePlaceholder')} {...slotProps('timeA')} />
      </div>
    );
  }

  return (
    <Field
      label={column.header}
      value={draft.a}
      onValueChange={(value) => onDraftChange({ ...draft, a: value })}
      placeholder={mode === 'date' ? datePlaceholder(locale) : numberPlaceholder}
      inputMode={mode === 'number' ? 'decimal' : 'text'}
      {...slotProps('a')}
    />
  );
}

/* -----------------------------------------------------------------------------
   The table
   -------------------------------------------------------------------------- */

const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

/** The footer's default cell content is "the op name + per-column formatter"
 *  (§11, `aggregate`), so the op needs a human spelling — held as catalogue KEYS
 *  and resolved with `t` where the cell is rendered. */
const OP_NAMES: Record<AggregationOp, string> = {
  sum: 'ds.table.aggSum',
  avg: 'ds.table.aggAvg',
  min: 'ds.table.aggMin',
  max: 'ds.table.aggMax',
  count: 'ds.table.aggCount',
  countDistinct: 'ds.table.aggDistinct',
  median: 'ds.table.aggMedian',
  stddev: 'ds.table.aggStddev',
};

/** `dhd-target-tight`, not `dhd-target`: four 32px buttons `gap-1` apart would
 *  have 44px overlays that overlap by 8px per pair, so the button to the right
 *  would steal its neighbour's taps. The tight variant keeps the 24px floor that
 *  WCAG 2.5.8 (AA) names and is what `global.css` reserves for a dense cluster. */
const PAGER_CLASS = cn(
  'dhd-target-tight relative inline-grid size-8 place-items-center rounded-xs',
  'border border-line text-body hover:bg-surface-muted hover:text-heading',
  PRESS,
  'disabled:pointer-events-none disabled:opacity-45',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
);

type Status = 'loading' | 'error' | 'success';

interface FetchState<T extends Row> {
  status: Status;
  result: TableResult<T> | null;
  error: string | null;
}

export function PaginatedTable<T extends Row = Row>({
  id,
  caption,
  columns,
  dataSource,
  rowKey,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  defaultPageSize = 25,
  columnsVersion = 1,
  selectSources,
  locale,
  onQueryChange,
  emptyTitle = 'No rows to show',
  emptyDescription,
  errorTitle = 'Could not load the rows',
  className,
  ...rest
}: PaginatedTableProps<T>) {
  const { t } = useTranslation();
  const activeLocale = resolveLocale(locale);
  const rowKeyColumn = rowKey ?? columns[0]?.key ?? 'id';
  const isNarrow = useMediaQuery(NARROW_QUERY);
  // A DOM id generated here, NOT the `id` prop: §11 states the table `id` is used
  // only as the persistence key, so the two identifiers stay separate.
  const domId = useId();
  const filterErrorId = `${domId}-filter-error`;

  const [page, setPage] = useState(1);
  // The default is clamped against the options too: an unlisted default would
  // leave the page-size `<select>` with no matching option (rendering blank)
  // while still sending that size to the API.
  const [pageSize, setPageSize] = useState(() => {
    const options = pageSizeOptions.length > 0 ? pageSizeOptions : DEFAULT_PAGE_SIZES;
    return readPageSize(
      id,
      options,
      options.includes(defaultPageSize) ? defaultPageSize : (options[0] ?? defaultPageSize),
    );
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<FilterSpec[]>([]);
  const [visibility, setVisibility] = useState<ColumnVisibilityState>(() =>
    readVisibility(id, columns, columnsVersion, rowKeyColumn),
  );
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<FetchState<T>>({ status: 'loading', result: null, error: null });

  // Two independent surfaces: a column's filter panel and the column selector.
  // Opening either closes the other, so no cross-talk between their dismiss events.
  const [filterKey, setFilterKey] = useState<string | null>(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [draft, setDraft] = useState<FilterDraft>(EMPTY_DRAFT);
  const [draftError, setDraftError] = useState<{ text: string; slot: DraftSlot | null } | null>(null);
  const [goToValue, setGoToValue] = useState('');

  const configByKey = useMemo(() => new Map(columns.map((column) => [column.key, column])), [columns]);

  // A page-size `<select>` must always have options to render. An empty list
  // falls back to the defaults, and a `pageSize` that is not in the list is
  // added to it — it IS the current size — so the control can never come up
  // blank while a size is still being sent to the API.
  const sizeOptions = useMemo(() => {
    const list = pageSizeOptions.length > 0 ? pageSizeOptions : DEFAULT_PAGE_SIZES;
    if (list.includes(pageSize)) return list;
    return [...list, pageSize].sort((a, b) => a - b);
  }, [pageSizeOptions, pageSize]);

  /* ---------------------------------------------------------------------------
     The query. `total`, `aggregations` and the rows always come from the API.
     ------------------------------------------------------------------------ */

  const sortField = sorting[0]?.id ?? null;
  const sortDesc = sorting[0]?.desc ?? false;
  const sort = useMemo<SortSpec | null>(
    () => (sortField ? { field: sortField, dir: sortDesc ? 'desc' : 'asc' } : null),
    [sortField, sortDesc],
  );

  const aggregations = useMemo<AggregationSpec[]>(
    () =>
      columns.flatMap((column) => {
        const aggregate = column.aggregate;
        // A hidden aggregated column must not be in `aggregations`, so the API
        // computes exactly the subtotals the table can actually render.
        if (!aggregate || visibility[column.key] === false) return [];
        return [{ field: column.key, op: aggregate.op, precision: aggregate.precision }];
      }),
    [columns, visibility],
  );

  const query = useMemo<TableQuery>(
    () => ({ page, pageSize, sort, filters, aggregations }),
    [page, pageSize, sort, filters, aggregations],
  );

  // The fetch is keyed on the query's serialized shape rather than on the query
  // object's identity: a page that passes an inline `columns` array would give
  // `aggregations` (and therefore `query`) a new identity on every render, and
  // every render would fire a request. Two queries that serialize the same are
  // the same request.
  const querySignature = JSON.stringify(query);

  const dataSourceRef = useRef(dataSource);
  dataSourceRef.current = dataSource;
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;

  /* The fetch. Idempotent under StrictMode: the cleanup aborts, so the double
     invocation leaves exactly one live request. `attempt` is the retry lever —
     it re-runs this effect WITHOUT touching the query, so a retry replays the
     last failed request exactly (same page, size, sort and filters). */
  useEffect(() => {
    const controller = new AbortController();

    // The stale-data rule: every fetch clears the body to the skeleton and the
    // region goes aria-busy. Rows from a previous response are never left up.
    setState({ status: 'loading', result: null, error: null });
    onQueryChangeRef.current?.(query);

    dataSourceRef.current(query, controller.signal).then(
      (result) => {
        if (controller.signal.aborted) return;
        setState({ status: 'success', result, error: null });
      },
      (caught: unknown) => {
        // A response that arrives after its request was cancelled is discarded,
        // so an out-of-order reply cannot clobber a newer one.
        if (controller.signal.aborted) return;
        if (caught instanceof Error && caught.name === 'AbortError') return;
        setState({ status: 'error', result: null, error: messageOf(caught) });
      },
    );

    return () => controller.abort();
  }, [querySignature, attempt]);

  /* ---------------------------------------------------------------------------
     Derived page arithmetic. The server clamps too; this is the table's own
     clamp, so a stale page can never render rows past the last page.
     ------------------------------------------------------------------------ */

  const total = state.result?.total ?? 0;
  const rows = state.result?.rows ?? [];
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.max(1, Math.min(page, lastPage));
  const hasRows = state.status === 'success' && total > 0;
  const filtersActive = filters.length > 0;

  const visibleColumns = useMemo(
    () => columns.filter((column) => visibility[column.key] !== false),
    [columns, visibility],
  );
  // The subtotal row renders only when a VISIBLE column has an aggregate: a
  // hidden aggregated column is not in the request, so it has no subtotal.
  const hasSubtotal = useMemo(
    () => visibleColumns.some((column) => column.aggregate !== undefined),
    [visibleColumns],
  );

  const columnDefs = useMemo(
    () =>
      columns.map((column) => ({
        id: column.key,
        header: column.header,
        accessorFn: (row: T) => row[column.key],
        enableSorting: column.sortable === true,
        // The row-key column is always visible and cannot be deselected.
        enableHiding: column.key !== rowKeyColumn,
      })),
    [columns, rowKeyColumn],
  );

  const onColumnVisibilityChange: OnChangeFn<ColumnVisibilityState> = useCallback(
    (updater) => {
      const next: ColumnVisibilityState =
        typeof updater === 'function'
          ? (updater as (previous: ColumnVisibilityState) => ColumnVisibilityState)(visibility)
          : updater;
      next[rowKeyColumn] = true;
      setVisibility(next);
      writeVisibility(id, columns, next, columnsVersion);
      setPage(1);
    },
    [columns, columnsVersion, id, rowKeyColumn, visibility],
  );

  const table = useTable({
    features: TABLE_FEATURES,
    columns: columnDefs,
    data: rows,
    state: { sorting, columnVisibility: visibility },
    onSortingChange: (updater) => {
      setSorting((previous) =>
        typeof updater === 'function' ? (updater as (p: SortingState) => SortingState)(previous) : updater,
      );
    },
    onColumnVisibilityChange,
    manualSorting: true,
    enableSortingRemoval: false,
  });

  const headerGroup = table.getHeaderGroups()[0];

  /* ---------------------------------------------------------------------------
     Actions — each one is what the §11 behaviour matrix names.
     ------------------------------------------------------------------------ */

  /* asc → desc on the same column, and a different column replaces the sort. */
  const cycleSort = (key: string) => {
    setSorting((previous) => {
      const current = previous[0];
      if (!current || current.id !== key) return [{ id: key, desc: false }];
      return [{ id: key, desc: !current.desc }];
    });
    setPage(1);
  };

  const goToPage = (next: number) => setPage(Math.max(1, Math.min(next, lastPage)));

  const openFilter = (key: string) => {
    const column = configByKey.get(key);
    if (!column) return;
    setDraft(draftFromFilter(column, filters.find((filter) => filter.field === key), activeLocale));
    setDraftError(null);
    setColumnsOpen(false);
    setFilterKey(key);
  };

  const closeFilter = () => {
    setFilterKey(null);
    setDraftError(null);
  };

  const applyDraft = () => {
    if (!filterKey) return;
    const column = configByKey.get(filterKey);
    if (!column) return;
    const result = buildFilter(column, draft, activeLocale);
    if (!result.ok) {
      setDraftError({ text: result.error ?? 'ds.table.errorValidValue', slot: result.slot });
      return;
    }
    setFilters((previous) => replaceFieldFilter(previous, column.key, result.spec));
    setPage(1);
    closeFilter();
  };

  const clearField = (key: string) => {
    setFilters((previous) => replaceFieldFilter(previous, key, null));
    setPage(1);
    closeFilter();
  };

  const clearAllFilters = () => {
    setFilters([]);
    setPage(1);
  };

  const changePageSize = (next: number) => {
    setPageSize(next);
    writePageSize(id, next);
    setPage(1);
  };

  /* ---------------------------------------------------------------------------
     Announcements — state changes are announced from one polite live region.
     ------------------------------------------------------------------------ */

  const announcement =
    state.status === 'loading'
      ? t('ds.table.announceLoading')
      : state.status === 'error'
        ? t('ds.table.announceError', { message: state.error ?? '' })
        : total === 0
          ? filtersActive
            ? t('ds.table.announceNoRowsFiltered')
            : t('ds.table.announceNoRows')
          : t('ds.table.announceDisplaying', { count: total, shown: rows.length, total });

  const activeFilterColumns = filters
    .map((filter) => configByKey.get(filter.field)?.header ?? filter.field)
    .join(', ');

  /* ---------------------------------------------------------------------------
     Horizontal overflow — the scroll affordance.
     ---------------------------------------------------------------------------
     A table wider than its wrapper scrolls, and on macOS the scrollbar is an
     overlay that takes no space and hides itself (measured on this build:
     `offsetHeight - clientHeight` is 0 for a 900px child in a 300px
     `overflow-x: auto` box, with and without `scrollbar-width`). So a reader
     looking at a 2039px table in a 1076px wrapper has no cue that six more
     columns exist to the right. The edge shadow below is that cue, and it is
     driven by the live scroll position rather than assumed — it appears only
     while there is more table to the right and disappears at the end.
     ------------------------------------------------------------------------ */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [overflowEnd, setOverflowEnd] = useState(false);

  const syncOverflow = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    const max = element.scrollWidth - element.clientWidth;
    setOverflowing(max > 1);
    setOverflowEnd(max > 1 && element.scrollLeft < max - 1);
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return undefined;
    syncOverflow();
    element.addEventListener('scroll', syncOverflow, { passive: true });
    const observer = new ResizeObserver(syncOverflow);
    observer.observe(element);
    return () => {
      element.removeEventListener('scroll', syncOverflow);
      observer.disconnect();
    };
  }, [syncOverflow, visibleColumns.length, state.status]);

  /* ---------------------------------------------------------------------------
     Render
     ------------------------------------------------------------------------ */

  return (
    <div {...rest} className={cn('flex min-w-0 flex-col gap-3', className)}>
      <p role="status" aria-live="polite" className="dhd-sr-only">
        {announcement}
      </p>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {filtersActive ? (
            <>
              <span className="text-2xs uppercase tracking-label text-muted">
                {t('ds.table.filters')} {activeFilterColumns}
              </span>
              <button
                type="button"
                onClick={clearAllFilters}
                className={cn(
                  'dhd-target relative inline-flex h-7 items-center gap-1 rounded-xs px-2 text-2xs',
                  'text-brand underline underline-offset-2 hover:text-heading',
                  PRESS,
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                )}
              >
                {t('ds.table.clearAllFilters')}
              </button>
            </>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          {/* The cue. It is the only thing on screen that says the table
              continues to the right, because the scrollbar does not: on macOS
              it is an overlay that takes no space and hides itself (measured —
              `offsetHeight - clientHeight` is 0 for a 900px child in a 300px
              `overflow-x: auto` box, with and without `scrollbar-width`).
              Right-aligned over the table's right edge, and driven by the live
              scroll position so it is absent when there is nothing more to see. */}
          {overflowing ? (
            <span
              className={cn(
                'inline-flex items-center gap-1 text-2xs uppercase tracking-label text-muted',
                !overflowEnd && 'invisible',
              )}
            >
              {t('ds.table.scrollForMoreColumns')}
              <IconChevronRight className="size-3" aria-hidden="true" />
            </span>
          ) : null}

          <FilterSurface
            open={columnsOpen}
            onOpenChange={(open) => {
              setColumnsOpen(open);
              if (open) setFilterKey(null);
            }}
            isNarrow={isNarrow}
            triggerClassName={cn(
              'dhd-target relative inline-flex h-8 items-center gap-1.5 rounded-xs px-3',
              'border border-line bg-surface text-2xs font-semibold uppercase tracking-label text-body',
              'hover:bg-surface-muted hover:text-heading',
              PRESS,
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
            )}
            triggerLabel={t('ds.table.chooseColumns')}
          triggerContent={
            <>
              <IconColumns3 className="size-4" aria-hidden="true" />
              {t('ds.table.columns')}
            </>
          }
          panelTitle={t('ds.table.columns')}
          panelDescription={t('ds.table.columnsPanelDescription')}
          footer={
            // "shown" claimed something the panel cannot know: at a 1076px
            // wrapper a 2039px table shows nine of its thirteen columns, so
            // "13 of 13 shown" was false at exactly the width where it mattered.
            // `enabled` is what the checkbox states mean, and it is true.
            <p className="text-2xs text-muted">
              {t('ds.table.columnsEnabled', {
                count: columns.length,
                visible: visibleColumns.length,
                total: columns.length,
              })}
            </p>
          }
        >
          <ul className="flex flex-col gap-1">
            {columns.map((column) => {
              const isRowKey = column.key === rowKeyColumn;
              const checked = visibility[column.key] !== false;
              return (
                <li key={column.key}>
                  {/* The whole row IS the checkbox: a `<label>` cannot be associated
                      with Base UI's non-native control, so a label-wrapped row would
                      look clickable and do nothing. */}
                  <Checkbox.Root
                    checked={checked}
                    disabled={isRowKey}
                    onCheckedChange={(next) => onColumnVisibilityChange({ ...visibility, [column.key]: next })}
                    className={cn(
                      'group flex w-full items-center gap-2.5 rounded-xs px-2 py-1.5 text-sm',
                      // Muted tokens, not an opacity step — the same rule the
                      // rest of the system's disabled states follow.
                      isRowKey ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-surface-muted',
                      !isRowKey && PRESS,
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'grid size-4 shrink-0 place-items-center rounded-xs border border-line-strong bg-surface',
                        'group-data-[checked]:border-primary group-data-[checked]:bg-primary',
                      )}
                    >
                      <Checkbox.Indicator>
                        <svg viewBox="0 0 16 16" className="size-3 text-on-primary" aria-hidden="true">
                          <path
                            d="M3.5 8.5 6.5 11.5 12.5 4.5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="square"
                          />
                        </svg>
                      </Checkbox.Indicator>
                    </span>
                    <span className={cn(isRowKey ? 'text-muted' : 'text-heading')}>
                      {column.header}
                    </span>
                    {/* The reason the row cannot be unchecked, written where a
                        sighted user can read it. It was `dhd-sr-only`, so the
                        only affordance on screen was a greyed-out tick — which
                        says "you cannot", never "why". Still un-hidden from the
                        accessibility tree, so the name is unchanged. */}
                    {isRowKey ? (
                      <span className="ml-auto text-2xs uppercase tracking-label text-muted">
                        {t('ds.table.alwaysVisible')}
                      </span>
                    ) : null}
                  </Checkbox.Root>
                </li>
              );
            })}
          </ul>
          </FilterSurface>
        </div>
      </div>

      <div
        ref={scrollRef}
        className={cn(
          'relative w-full overflow-x-auto rounded-xs border border-line bg-surface',
          !hasRows && 'min-h-[var(--control-h-lg)]',
        )}
        aria-busy={state.status === 'loading' || undefined}
      >
        <table className="w-full border-collapse text-sm">
          <caption className="border-b border-line-subtle px-4 py-2.5 text-left font-display text-sm font-semibold uppercase tracking-title text-heading">
            {caption}
          </caption>

          <thead>
            {headerGroup ? (
              <tr className="h-12 border-b-2 border-line-strong">
                {headerGroup.headers.map((header) => {
                  const column = configByKey.get(header.column.id);
                  if (!column) return null;
                  const sorted = header.column.getIsSorted();
                  const canSort = header.column.getCanSort();
                  const hasFilter = filters.some((filter) => filter.field === column.key);
                  const width =
                    typeof column.width === 'number' ? `${column.width}px` : column.width;

                  return (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : canSort ? 'none' : undefined}
                      style={width ? { width, minWidth: width } : undefined}
                      className={cn(
                        'bg-surface-alt px-3 py-2 font-display text-2xs font-semibold uppercase tracking-label text-muted',
                        // The row key column is pinned. Scrolling right used to
                        // take the contractor's name off screen and leave six
                        // columns of numbers that belonged to no identifiable
                        // row. `z-20` keeps it above the body's pinned cells.
                        column.key === rowKeyColumn &&
                          'sticky left-0 z-20 border-r border-line',
                        alignOf(column) === 'right' && 'text-right',
                        alignOf(column) === 'center' && 'text-center',
                        alignOf(column) === 'left' && 'text-left',
                      )}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {canSort ? (
                          <button
                            type="button"
                            onClick={() => cycleSort(column.key)}
                            className={cn(
                              'dhd-target-tight relative inline-flex items-center gap-1 rounded-xs px-1 py-0.5',
                              'hover:text-heading',
                              PRESS,
                              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                            )}
                          >
                            <span className="dhd-sr-only">{t('ds.table.sortBy')} </span>
                            {column.header}
                            <span className="text-brand" aria-hidden="true">
                              {sorted === 'asc' ? (
                                <IconCaretUpFilled className="size-3" />
                              ) : sorted === 'desc' ? (
                                <IconCaretDownFilled className="size-3" />
                              ) : (
                                <IconArrowsSort className="size-3 opacity-60" />
                              )}
                            </span>
                          </button>
                        ) : (
                          <span className="px-1">{column.header}</span>
                        )}

                        {column.filterable ? (
                          <FilterSurface
                            open={filterKey === column.key}
                            onOpenChange={(open) => (open ? openFilter(column.key) : closeFilter())}
                            isNarrow={isNarrow}
                            triggerClassName={cn(
                              'dhd-target-tight relative inline-grid size-6 shrink-0 place-items-center rounded-xs',
                              'hover:bg-surface-muted hover:text-heading',
                              PRESS,
                              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                              hasFilter ? 'text-brand' : 'text-muted',
                            )}
                            triggerLabel={
                              // The `aria-label` is the control's whole name, so the
                              // active state has to live in it — the dot is only the
                              // visual half of the same signal.
                              hasFilter
                                ? t('ds.table.filterColumnActive', { column: column.header })
                                : t('ds.table.filterColumn', { column: column.header })
                            }
                            triggerContent={
                              <>
                                <IconFilter className="size-3.5" aria-hidden="true" />
                                {hasFilter ? (
                                  <span
                                    className="absolute right-0.5 top-0.5 size-1.5 rounded-pill bg-brand"
                                    aria-hidden="true"
                                  />
                                ) : null}
                              </>
                            }
                            panelTitle={t('ds.table.filterColumn', { column: column.header })}
                            panelDescription={isRange(column) ? t('ds.table.enterBothBounds') : undefined}
                            footer={
                              <>
                                <button
                                  type="button"
                                  onClick={() => clearField(column.key)}
                                  disabled={!hasFilter}
                                  className={cn(
                                    'dhd-target relative inline-flex h-8 items-center rounded-xs px-3 text-2xs uppercase tracking-label',
                                    'border border-line text-body hover:bg-surface-muted hover:text-heading',
                                    PRESS,
                                    'disabled:pointer-events-none disabled:opacity-45',
                                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                                  )}
                                >
                                  {t('ds.table.clear')}
                                </button>
                                <button
                                  type="button"
                                  onClick={applyDraft}
                                  className={cn(
                                    'dhd-target relative inline-flex h-8 items-center rounded-xs px-3 text-2xs font-semibold uppercase tracking-label',
                                    'border border-primary bg-primary text-on-primary hover:bg-primary-hover',
                                    PRESS,
                                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                                  )}
                                >
                                  {t('ds.table.apply')}
                                </button>
                              </>
                            }
                          >
                            <FilterEditor
                              column={column}
                              draft={draft}
                              onDraftChange={(next) => {
                                setDraft(next);
                                setDraftError(null);
                              }}
                              errorSlot={draftError?.slot ?? null}
                              errorId={filterErrorId}
                              locale={activeLocale}
                              selectSource={selectSources?.[column.key]}
                            />
                            {draftError ? (
                              <p id={filterErrorId} role="alert" className="mt-3 text-2xs text-danger">
                                {t(draftError.text)}
                              </p>
                            ) : null}
                          </FilterSurface>
                        ) : null}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ) : null}
          </thead>

          {state.status === 'loading' ? (
            <tbody>
              <tr>
                <td colSpan={Math.max(visibleColumns.length, 1)} className="p-0 align-top">
                  {/* The real `<thead>` stays mounted, so the placeholder supplies
                      body rows only — and matches the 48px row height exactly. */}
                  <SkeletonTable
                    rows={Math.min(pageSize, 10)}
                    columns={Math.max(visibleColumns.length, 1)}
                    showHeader={false}
                    className="rounded-none border-0"
                  />
                </td>
              </tr>
            </tbody>
          ) : state.status === 'error' ? (
            <tbody>
              <tr>
                <td colSpan={Math.max(visibleColumns.length, 1)} className="p-0 align-top">
                  <ErrorState
                    variant="table"
                    title={errorTitle}
                    message={state.error ?? t('ds.table.errorFallback')}
                    onRetry={() => setAttempt((value) => value + 1)}
                  />
                </td>
              </tr>
            </tbody>
          ) : total === 0 ? (
            <tbody>
              <tr>
                <td colSpan={Math.max(visibleColumns.length, 1)} className="p-0 align-top">
                  <EmptyState
                    variant="table"
                    title={emptyTitle}
                    description={
                      filtersActive
                        ? (emptyDescription ?? 'No rows match the active filters.')
                        : (emptyDescription ?? 'There is nothing to display yet.')
                    }
                    filtersActive={filtersActive}
                    onClearFilters={clearAllFilters}
                  />
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={String(row[rowKeyColumn] ?? index)}
                  // No entrance animation: a table is on the list of components
                  // with no natural animated state, and a stagger here would
                  // delay the rows a fetch just returned.
                  // 48px rows, matching `--control-h-lg` and therefore the table
                  // skeleton that replaces these rows while a fetch is in flight.
                  className="group h-12 border-b border-line-subtle hover:bg-surface-alt"
                >
                  {visibleColumns.map((column) => {
                    const value = row[column.key];
                    const text = column.formatter ? column.formatter(value, row) : cellText(value);
                    return (
                      <td
                        key={column.key}
                        className={cn(
                          'px-3 py-2 text-body',
                          // Pinned like its header, and opaque for the same
                          // reason: a transparent pinned cell would let the
                          // columns underneath show through as it travels.
                          // `group-hover` keeps the row highlight continuous.
                          column.key === rowKeyColumn &&
                            'sticky left-0 z-10 border-r border-line bg-surface group-hover:bg-surface-alt',
                          alignOf(column) === 'right' && 'text-right',
                          alignOf(column) === 'center' && 'text-center',
                        )}
                      >
                        {text}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          )}

          {hasRows && hasSubtotal ? (
            <tfoot>
              <tr className="border-t-2 border-line-strong bg-surface-alt">
                {visibleColumns.map((column, index) => {
                  const config = configByKey.get(column.key);
                  const aggregate = config?.aggregate;
                  const aggregateValue = state.result?.aggregations[column.key];
                  const valueText =
                    aggregateValue === null || aggregateValue === undefined
                      ? '—'
                      : config?.formatter
                        ? config.formatter(aggregateValue)
                        : formatNumber(aggregateValue, {
                            locale,
                            // The API already rounded to the declared `precision`;
                            // the formatter's own default of 0 would show an `avg`
                            // the server computed as 12.6 as "13".
                            precision: aggregate?.precision ?? decimalsOf(aggregateValue),
                          });
                  const content = aggregate
                    ? (aggregate.label ?? valueText)
                    : index === 0
                      ? t('ds.table.subtotal')
                      : '';

                  const cell = (
                    <>
                      {index === 0 ? <span className="dhd-sr-only">{t('ds.table.subtotal')} </span> : null}
                      {aggregate ? <span className="dhd-sr-only">{column.header}: </span> : null}
                      {aggregate && !aggregate.label ? (
                        <span className="mr-1.5 text-2xs uppercase tracking-label text-muted">
                          {t(OP_NAMES[aggregate.op])}
                        </span>
                      ) : null}
                      {content}
                    </>
                  );

                  return index === 0 ? (
                    <th
                      key={column.key}
                      scope="row"
                      className={cn(
                        'px-3 py-2 font-display text-2xs font-semibold uppercase tracking-label text-heading',
                        column.key === rowKeyColumn &&
                          'sticky left-0 z-20 border-r border-line bg-surface-alt',
                        alignOf(column) === 'right' && 'text-right',
                        alignOf(column) === 'center' && 'text-center',
                        alignOf(column) === 'left' && 'text-left',
                      )}
                    >
                      {cell}
                    </th>
                  ) : (
                    <td
                      key={column.key}
                      className={cn(
                        'px-3 py-2 text-sm text-heading',
                        alignOf(column) === 'right' && 'text-right',
                        alignOf(column) === 'center' && 'text-center',
                      )}
                    >
                      {cell}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      {hasRows ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-2xs text-muted">
            {t('ds.table.displaying', {
              count: total,
              from: (currentPage - 1) * pageSize + 1,
              to: Math.min(currentPage * pageSize, total),
              total,
            })}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Label className="gap-2">
              <span>{t('ds.table.rowsPerPage')}</span>
              {/* `appearance-none` plus a token chevron: a native `<select>`'s own
                  indicator is the one part a component cannot restyle, so the
                  whole control is drawn here rather than left browser-default.

                  Geometry is the forms family's — --control-h-md (40px) on the
                  1px token border, the same box Select and Input draw — and the
                  wrapper carries `dhd-target` so the 44px hit area survives the
                  control being genuinely 40px tall (CONVENTIONS §6: a replaced
                  element cannot render the overlay itself). */}
              <span className="dhd-target relative inline-flex items-center">
                <select
                  value={pageSize}
                  onChange={(event) => changePageSize(Number(event.target.value))}
                  className={cn(
                    'relative z-10 h-[var(--control-h-md)] appearance-none rounded-xs border-[length:var(--border-w)] border-line-strong bg-surface pl-2 pr-7 text-xs text-heading',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                  )}
                >
                  {sizeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <IconCaretDownFilled
                  className="pointer-events-none absolute right-2 size-3 text-muted"
                  aria-hidden="true"
                />
              </span>
            </Label>

            <div className="flex items-center gap-2">
              <Label htmlFor={`${domId}-go-to-page`}>{t('ds.table.goToPage')}</Label>
              {/* The same family geometry as the page-size select beside it, with
                  the 44px hit area carried by the wrapper. */}
              <span className="dhd-target relative inline-flex">
                <input
                  id={`${domId}-go-to-page`}
                  type="text"
                  inputMode="numeric"
                  value={goToValue}
                  placeholder={String(currentPage)}
                  onChange={(event) => setGoToValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return;
                    event.preventDefault();
                    const parsed = Number(goToValue);
                    if (Number.isFinite(parsed) && parsed > 0) goToPage(Math.trunc(parsed));
                    setGoToValue('');
                  }}
                  className={cn(
                    'relative z-10 h-[var(--control-h-md)] w-16 rounded-xs border-[length:var(--border-w)] border-line-strong bg-surface px-2 text-xs text-heading',
                    'placeholder:text-muted',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                  )}
                />
              </span>
              <button
                type="button"
                onClick={() => {
                  const parsed = Number(goToValue);
                  if (Number.isFinite(parsed) && parsed > 0) goToPage(Math.trunc(parsed));
                  setGoToValue('');
                }}
                className={cn(
                  'dhd-target relative inline-flex h-8 items-center rounded-xs px-3 text-2xs uppercase tracking-label',
                  'border border-line text-body hover:bg-surface-muted hover:text-heading',
                  PRESS,
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                )}
              >
                {t('ds.table.go')}
              </button>
            </div>

            <div className="flex items-center gap-1">
              <span className="px-1 text-2xs text-muted">
                {t('ds.table.page', { count: lastPage, current: currentPage, last: lastPage })}
              </span>
              <button
                type="button"
                aria-label={t('ds.table.firstPage')}
                disabled={currentPage <= 1}
                onClick={() => goToPage(1)}
                className={PAGER_CLASS}
              >
                <IconChevronsLeft className="size-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={t('ds.table.previousPage')}
                disabled={currentPage <= 1}
                onClick={() => goToPage(currentPage - 1)}
                className={PAGER_CLASS}
              >
                <IconChevronLeft className="size-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={t('ds.table.nextPage')}
                disabled={currentPage >= lastPage}
                onClick={() => goToPage(currentPage + 1)}
                className={PAGER_CLASS}
              >
                <IconChevronRight className="size-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={t('ds.table.lastPage')}
                disabled={currentPage >= lastPage}
                onClick={() => goToPage(lastPage)}
                className={PAGER_CLASS}
              >
                <IconChevronsRight className="size-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
